/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button, Checkbox, Input, message, Spin, Typography } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import Modal from 'antd/lib/modal/Modal';
import Tree, { DataNode } from 'antd/lib/tree';
import { uniq } from 'lodash';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { __DEV__ } from '~src/config';
import navigationItems from '~src/data/navigationItems';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  copyFiles,
  createCommit,
  createFolder,
  deleteCommit,
  loadFiles,
  updateCommit,
} from '~src/services/api';
import { fadeInKeyframes } from '~src/styles/animations';
import { DbCommit, DbFile, DbFolder } from '~src/types/db';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { parseMessagePreview } from '~src/utils/messagePreview';
import { queryCache, useMutation } from 'react-query';
import { updateFoldersEmpty } from '~src/utils/foldersListUpdateEmpty';

export interface CopyModalProps extends Partial<ModalProps> {
  folders?: DbFolder[];
  files?: DbFile[];
  /** current folderId containing items */
  folderId: DbFile['folder_id'];
}

const archiveKey = 'archive';
const archiveNode = navigationItems.find(
  (v) => v.key === archiveKey
) as typeof navigationItems[0];

type StateDataNode = DataNode & {
  children?: StateDataNode[];
  parentId?: string | null;
};

const foldersQueryBase = {
  as_admin:
    window.sessionStorage.getItem('appState') === 'superUser'
      ? true
      : undefined,
  before:
    window.sessionStorage.getItem('timeMachineDate') !== null
      ? Number(window.sessionStorage.getItem('timeMachineDate'))
      : undefined,
};

export const CopyModal: React.FC<CopyModalProps> = ({
  folders = [],
  files = [],
  folderId,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const foldersQuery = useFolders(foldersQueryBase, { enabled: false });

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    folderId ? String(folderId) : null
  );

  const filesQuery = useFiles(
    { folderId: selectedFolderId },
    { enabled: files.length > 0 && modalProps.visible }
  );

  const [selectedKeys, setSelectedKeys] = useState<string[]>([archiveKey]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([archiveKey]);

  const [step, setStep] = useState<1 | 2>(1);
  const [commitMessage, setCommitMessage] = useState(
    __DEV__ ? 'This is a test commit' : ''
  );
  const [importantVal, setImportantVal] = useState(false);

  const [treeData, setTreeData] = useState<StateDataNode[]>([
    {
      title: intl.formatMessage({ id: archiveNode.nameTerm }),
      key: archiveNode.key,
      icon: (
        <IconButton
          icon={archiveNode.icon as IconProp}
          color={archiveNode.color}
          customStyle={styles.icons}
          backgroundOff
        />
      ),
    },
  ]);

  // keep `selectedFolderId` updated
  useEffect(() => {
    if (modalProps.visible) {
      setSelectedFolderId(folderId ? String(folderId) : null);

      const selectedKey = folderId ? String(folderId) : archiveKey;
      setSelectedKeys([selectedKey]);
      setExpandedKeys([selectedKey]);
    }
  }, [modalProps.visible, folderId]);

  useEffect(() => {
    if (modalProps.visible) {
      // fetch folders if data is older than 1min
      if (Date.now() - foldersQuery.updatedAt > 60 * 1000) {
        foldersQuery.refetch();
      }
    }
  }, [modalProps.visible, foldersQuery]);

  // set treeData based on foldersQuery
  useEffect(() => {
    if (foldersQuery.data) {
      setTreeData((prevData) => {
        const nextData = [...prevData];

        nextData[0].children = arrToTree({
          arr: foldersQuery.data || [],
          parentId: null,
        });

        return nextData;
      });
    }
  }, [foldersQuery.data]);

  const [doCopyRegistrations, mutationCopyRegistrations] = useMutation(
    copyFiles,
    {
      onError: (error) => {
        appLog('CopyRegistrations => ', error);
        message.error(intl.formatMessage({ id: 'copy-failed' }));
        // @ts-ignore
        onCancel && onCancel();
      },
      onSuccess: (data, variables) => {
        queryCache.setQueryData<DbFolder[]>(
          ['folders', { ...foldersQueryBase, projectId: String(projectId) }],
          (prevFolders = []) => {
            return updateFoldersEmpty({
              folders: prevFolders,
              changedFolderId: variables.folderId,
            });
          }
        );
        queryCache.invalidateQueries('files');
      },
    }
  );

  useEffect(() => {
    if (modalProps.visible && step === 2) setStep(1);
  }, [modalProps.visible]);

  if (modalProps.visible && !files.length && !folders.length)
    throw new Error('CopyModal should be shown with files and/or folders!');

  interface CopyAllParams {
    folders?: DbFolder[];
    files?: DbFile[];
    selectedId: DbFile['folder_id'];
    commit?: DbCommit;
  }

  const copyAll = async ({
    folders = [],
    files = [],
    selectedId,
    commit,
  }: CopyAllParams) => {
    doCopyRegistrations({
      projectId: Number(projectId),
      files: files.map((item) => item.id),
      folderId: Number(selectedId),
      commit,
    });

    const foldersPromises = copyFolders({
      foldersList: foldersQuery.data,
      folders,
      selectedId,
      commit,
    });

    return Promise.all<DbFile | any>([...foldersPromises]);
  };

  const [doCopyAll, { isLoading }] = useMutation(copyAll, {
    onSuccess: async (data, { commit }) => {
      if (folders.length) {
        queryCache.invalidateQueries('folders');
      }

      if (files.length) {
        // refresh initial folder files
        queryCache.invalidateQueries('files');

        // refresh destination folder files
        queryCache.invalidateQueries(
          [
            'files',
            {
              projectId: String(projectId),
              folderId: selectedFolderId,
            },
          ],
          { refetchInactive: true }
        );
      }

      commit &&
        updateCommit({
          projectId: String(projectId),
          commitData: { ...commit, state: DB_STATE.PROCESSED },
        });

      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();
    },
    onError: (error, item) => {
      item.commit &&
        deleteCommit({
          projectId: String(projectId),
          commit: item.commit,
        }).catch(() => undefined);
      message.error(intl.formatMessage({ id: 'copy-failed' }));
      appLog('CopyModal -> editAll -> items', item);
      appLog('CopyModal -> editAll -> err', error);
    },
  });

  interface CopyFoldersParms {
    foldersList?: DbFolder[];
    folders: DbFolder[];
    selectedId: DbFolder['parent_id'] | DbFile['parent_id'];
    commit?: DbCommit;
  }

  const copyFolders = ({
    foldersList = [],
    folders,
    selectedId,
    commit,
  }: CopyFoldersParms) => {
    return folders.map(async (folder) => {
      try {
        const newFolder = await createFolder({
          projectId: String(projectId),
          folderData: {
            name: folder.name,
            description: folder.description,
            parent_id: selectedId,
            state: folder.state,
            is_locked: folder.is_locked,
          },
          commit_id: commit?.id,
        });

        const filesInOnldFolder = await loadFiles('files', {
          projectId: String(projectId),
          folderId: String(folder.id),
          withVersions: false,
        });

        filesInOnldFolder.length > 0 &&
          doCopyRegistrations({
            projectId: Number(projectId),
            files: filesInOnldFolder.map((item) => item.id),
            folderId: Number(newFolder.id),
            commit: commit,
          });

        const childFolders = foldersList.filter(
          (item) => item.parent_id === folder.id
        );

        return Promise.all(
          copyFolders({
            foldersList,
            folders: childFolders,
            selectedId: newFolder.id,
            commit,
          })
        );
      } catch (err) {
        appLog('eerroorr here -> ', err);
        throw err;
      }
    });
  };

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: commitMessage,
      is_important: importantVal,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('CopyItems -> createCommit -> commit', commitData);
      appLog('CopyItems -> createCommit -> err', err);
      throw err;
    });
  };

  const originalSelectedFolderId =
    selectedFolderId !== null ? Number(selectedFolderId) : selectedFolderId;

  // restrict destination based on permission (WRITE=4)
  const allowedDestination = useMemo(() => {
    if (!foldersQuery.data || originalSelectedFolderId === null) return false;

    const destinationFolder = foldersQuery.data.find(
      (v) => v.id === originalSelectedFolderId
    );
    if (!destinationFolder) return false;

    return destinationFolder.permission >= RIGHTS_LEVEL.WRITE;
  }, [foldersQuery.data, originalSelectedFolderId]);

  const isValid = () => {
    if (
      !allowedDestination ||
      !foldersQuery.data ||
      (files.length && !filesQuery.data) ||
      (!files.length && !folders.length)
    )
      return false;

    // files validation
    if (files.length) {
      // don't allow files on root
      if (selectedFolderId === null) return false;

      // check for files with matching names in selected folder
      if (
        files.some((file) =>
          filesQuery.data?.some(
            (v) => v.id !== file.id && v.title === file.title
          )
        )
      ) {
        return false;
      }
    }

    // folders validation
    if (folders.length) {
      const selectedId =
        selectedKeys[0] !== archiveKey ? Number(selectedKeys[0]) : null;

      return !folders.some((folder) =>
        foldersQuery.data?.some(
          (v) =>
            (v.parent_id === selectedId &&
              v.id !== folder.id &&
              v.name === folder.name) ||
            folder.id === selectedId
        )
      );
    }

    return true;
  };

  const handleSelect = (keys: React.Key[]) => {
    const strKeys = keys as string[];

    // triggers refetch of filesQuery
    setSelectedFolderId(strKeys[0] === archiveKey ? null : strKeys[0]);

    // set selected
    setSelectedKeys(strKeys);
    setExpandedKeys((prevKeys) => uniq([...prevKeys, ...strKeys]));
  };

  let singleEntity: DbFile | DbFolder | undefined;
  if (files.length <= 1 && !folders.length) {
    singleEntity = files[0];
  } else if (folders.length <= 1 && !files.length) {
    singleEntity = folders[0];
  }

  const handleSubmit = async () => {
    if ((!files.length && !folders.length) || !isValid() || !selectedKeys[0])
      return;
    const selectedId =
      selectedKeys[0] !== archiveKey ? Number(selectedKeys[0]) : null;

    if (selectedId === folderId) {
      // @ts-ignore
      onCancel && onCancel();
      return;
    }

    await doCreateCommit().then((commit) => {
      doCopyAll({
        files,
        folders,
        selectedId,
        commit,
      });
    });
  };

  const modalFooter = (
    <div style={{ marginTop: -10 }}>
      <Button
        type="default"
        onClick={step === 2 ? () => setStep(1) : onCancel}
        css={styles.buttons}>
        {step === 1
          ? intl.formatMessage({ id: 'cancel' })
          : intl.formatMessage({ id: 'back' })}
      </Button>

      <Button
        type="primary"
        css={styles.buttons}
        loading={isLoading}
        disabled={!isValid() || isLoading}
        onClick={step === 1 ? () => setStep(2) : handleSubmit}>
        {step === 1
          ? intl.formatMessage({ id: 'continue' })
          : intl.formatMessage({ id: 'save' })}
      </Button>
    </div>
  );

  return (
    <Modal
      title={
        <ModalTitle
          text={
            singleEntity
              ? intl.formatMessage(
                  { id: 'copy-single-modal-title' },
                  {
                    name: parseMessagePreview(
                      (singleEntity as DbFile).title ||
                        (singleEntity as DbFolder).name
                    ),
                  }
                )
              : intl.formatMessage({ id: 'copy-modal-title' })
          }
          icon={['far', 'arrow-alt-from-left']}
        />
      }
      centered
      destroyOnClose={true} // to reset expanded
      width={MODAL_WIDTH}
      onCancel={onCancel}
      footer={modalFooter}
      {...modalProps}>
      {step === 1 ? (
        <React.Fragment>
          <Typography.Paragraph css={styles.loadingContainer} type="danger">
            {filesQuery.isFetching || foldersQuery.isFetching ? (
              <Spin size="small" />
            ) : !allowedDestination ? (
              intl.formatMessage({ id: 'permission-restricted' })
            ) : !isValid() ? (
              intl.formatMessage({ id: 'name-conflict' })
            ) : null}
          </Typography.Paragraph>

          <div css={styles.treeContainer}>
            <Tree.DirectoryTree
              css={styles.tree}
              blockNode
              treeData={treeData}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              expandAction={false} // don't auto expand on click
              autoExpandParent
              onSelect={handleSelect}
              onExpand={(keys) => setExpandedKeys(keys as string[])}
            />
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Input.TextArea
            css={{
              width: '100%',
              animation: `${fadeInKeyframes} 0.3s ease-out`,
            }}
            rows={3}
            placeholder={intl.formatMessage({
              id: 'enter-copy-message',
            })}
            autoFocus
            value={commitMessage}
            onChange={({ target: { value } }) => setCommitMessage(value)}
          />

          <Checkbox
            css={styles.checkbox}
            checked={importantVal}
            onChange={({ target: { checked } }) => setImportantVal(checked)}>
            {intl.formatMessage({ id: 'select-copy-as-important' })}
          </Checkbox>
        </React.Fragment>
      )}
    </Modal>
  );
};

interface ArrToTreeParams {
  arr: DbFolder[];
  parentId: DbFolder['parent_id'];
}
/**
 * transforms db folder list in directory tree
 */
export const arrToTree = ({
  arr,
  parentId,
}: ArrToTreeParams): StateDataNode[] => {
  return arr
    .filter((dbFolder) => dbFolder.parent_id === parentId)
    .map((dbFolder) => {
      const treeObj = dbFolderToTreeObj({
        dbFolder,
      });

      return {
        ...treeObj,
        children: arrToTree({
          arr,
          parentId: dbFolder.id,
        }),
        createdAt: dbFolder.created_at,
        description: dbFolder.description,
        name: dbFolder.name,
      };
    });
};

const MODAL_WIDTH = 420;
const TREE_WIDTH = MODAL_WIDTH - 45;

const styleFn: ThemedStyleFn = (theme) => ({
  icons: {
    fontSize: '20px',
    transition: '0.3s',
    margin: '3px 0 10px -16px',
  },
  treeContainer: {
    minHeight: '30vh',
    maxHeight: '39vh',
    width: TREE_WIDTH,
    maxWidth: TREE_WIDTH,
    overflow: 'auto',
  },
  tree: {
    width: '100%',
    overflow: 'hidden',
  },
  loadingContainer: {
    '&.ant-typography': {
      minHeight: 24,
      marginBottom: '0.7em',
      marginTop: -20,
    },
  },
  buttons: {
    margin: ' 10px 10px 0 10px',
  },
  checkbox: {
    marginTop: 6,
    alignSelf: 'flex-start',
  },
});

const FolderIcon = (props) => {
  const styles = useStyles(styleFn);

  return (
    <IconButton
      iconStyle="fas"
      icon="folder"
      color="yellow"
      customStyle={styles.icons}
      backgroundOff
      {...props}
    />
  );
};

interface DbFolderToTreeObjParams {
  dbFolder: DbFolder;
}
const dbFolderToTreeObj = ({
  dbFolder,
}: DbFolderToTreeObjParams): StateDataNode => {
  const { id, name, parent_id } = dbFolder;

  return {
    title: name || 'Unknown',
    key: String(id),
    icon: <FolderIcon />,
    children: [],
    parentId: String(parent_id),
  };
};
